<template>
  <div class="incident-report-wizard d-flex flex-column" style="height: 100%;">
    <!-- Wizard Header -->
    <div class="wizard-header bg-secondary text-white px-4 py-3 d-flex align-items-center flex-shrink-0">
      <div>
        <h5 class="mb-0">Generate Incident Report</h5>
        <small v-if="event">
          {{ event.property && event.property.name }}
          <span v-if="event.number"> &middot; Incident #{{ event.number }}</span>
        </small>
      </div>
      <b-button class="ml-auto" variant="light" size="sm" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </b-button>
    </div>

    <!-- Step Indicator (selection & preview only) -->
    <div v-if="activeStep !== 'generating' && activeStep !== 'complete'"
         class="step-indicator d-flex border-bottom bg-light px-4 py-2 flex-shrink-0">
      <small
        :class="activeStep === 'selection' ? 'text-primary font-weight-bold' : 'text-muted'"
        class="mr-3"
      >
        <i class="fas fa-list-check mr-1"></i> 1. Configure
      </small>
      <small
        :class="activeStep === 'preview' ? 'text-primary font-weight-bold' : 'text-muted'"
      >
        <i class="fas fa-eye mr-1"></i> 2. Preview
      </small>
    </div>

    <!-- Scrollable Content Area -->
    <div class="wizard-body flex-grow-1 overflow-auto">

      <!-- Step: Selection -->
      <template v-if="activeStep === 'selection'">
        <IncidentReportOptionsPanel
          :event="event"
          :all-images="allEventImages"
          :render-options="renderOptions"
          :involved-people="involvedPeople"
          :event-videos="eventVideos"
          :event-documents="eventDocuments"
          :event-expenses="eventExpenses"
          :event-claims="eventClaims"
          :media-loading="mediaLoading"
          @update:renderOptions="renderOptions = $event"
        />

        <!-- Report History (shown at bottom of selection step) -->
        <div v-if="historyItems.length > 0" class="px-4 pb-4">
          <hr />
          <IncidentReportHistoryTable
            :history="historyItems"
            @regenerate="doRegenerateFromHistory"
          />
        </div>
      </template>

      <!-- Step: Preview -->
      <template v-if="activeStep === 'preview'">
        <IncidentReportPreviewPanel
          :preview-html="previewHtml"
          :is-loading="isLoadingPreview"
        />
      </template>

      <!-- Step: Generating / Complete -->
      <template v-if="activeStep === 'generating' || activeStep === 'complete'">
        <IncidentReportProgressTracker
          :progress-data="progressData"
          @view-report="doViewReport"
          @go-to-files="doGoToFiles"
          @generate-another="doGenerateAnother"
        />
      </template>

    </div>

    <!-- Wizard Footer -->
    <div v-if="activeStep === 'selection' || activeStep === 'preview'"
         class="wizard-footer border-top px-4 py-3 d-flex justify-content-between align-items-center flex-shrink-0 bg-white">
      <!-- Left: Back button (preview step only) -->
      <div>
        <b-button v-if="activeStep === 'preview'" variant="outline-secondary" @click="activeStep = 'selection'">
          <i class="fas fa-arrow-left mr-1"></i> Edit Selections
        </b-button>
      </div>
      <!-- Right: Action buttons -->
      <div class="d-flex" style="gap: 8px;">
        <b-button
          v-if="activeStep === 'selection'"
          variant="outline-secondary"
          :disabled="isLoadingPreview"
          @click="doPreviewReport"
        >
          <b-spinner v-if="isLoadingPreview" small class="mr-1"></b-spinner>
          <i v-else class="fas fa-eye mr-1"></i>
          Preview Report
        </b-button>
        <b-button
          variant="primary"
          :disabled="isLoading"
          @click="doGenerateReport"
        >
          <b-spinner v-if="isLoading" small class="mr-1"></b-spinner>
          <i v-else class="fas fa-file-pdf mr-1"></i>
          Generate PDF Report
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import IncidentReportOptionsPanel from './incident-report/IncidentReportOptionsPanel.vue'
import IncidentReportPreviewPanel from './incident-report/IncidentReportPreviewPanel.vue'
import IncidentReportProgressTracker from './incident-report/IncidentReportProgressTracker.vue'
import IncidentReportHistoryTable from './incident-report/IncidentReportHistoryTable.vue'

const DEFAULT_RENDER_OPTIONS = {
  includedPeopleIds: [],
  includedImageIds: [],
  includedVideoIds: [],
  includedDocumentIds: [],
  includedInsuranceClaimIds: [],
  includedExpenseIds: [],
  includeAISummary: true,
  includeQRCodes: true,
  includeVideos: true,
  includeTableOfContents: true,
  includeSignaturePage: false,
  includeConfidentialWatermark: false,
  includeSopSection: true,
  includeInsuranceClaims: true,
  includeExpenses: true,
  includeDocumentAppendix: true,
  mergeDocumentsIntoPdf: true,
  includeMediaCollectionQR: true
}

const MAX_POLL_ATTEMPTS = 100 // 5 minutes at 3s each

export default {
  name: 'IncidentReportWizard',
  components: {
    IncidentReportOptionsPanel,
    IncidentReportPreviewPanel,
    IncidentReportProgressTracker,
    IncidentReportHistoryTable
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  data () {
    const savedOptions = this.event.incidentReportRenderOptions || {}
    return {
      activeStep: 'selection',  // 'selection' | 'preview' | 'generating' | 'complete'
      renderOptions: {
        ...DEFAULT_RENDER_OPTIONS,
        ...savedOptions
      },
      involvedPeople: [],
      eventVideos: [],
      eventDocuments: [],
      eventExpenses: [],
      eventClaims: [],
      historyItems: [],
      previewHtml: null,
      progressData: null,
      generatingToken: null,
      pollInterval: null,
      pollAttempts: 0,
      mediaLoading: false,
      isLoadingPreview: false,
      isLoading: false
    }
  },
  created () {
    this.doLoadInitialData()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  computed: {
    qrCodeFeatureEnabled () {
      return this.$store.getters['featureFlags/isEnabled']('media.qrcode-sharing')
    },
    canViewQRCode () {
      return this.qrCodeFeatureEnabled &&
             (this.$store.state.loggedInUserPermissions && this.$store.state.loggedInUserPermissions.viewQRCode === true)
    },
    allEventImages () {
      const eventImages = this.event.images || []
      const sopImages = []
      ;(this.event.sopQuestions || []).forEach(q => {
        ;(q.images || []).forEach(img => sopImages.push(img))
      })
      return [...eventImages, ...sopImages]
    }
  },
  methods: {
    ...mapActions('events', [
      'getInvolvedPeople',
      'getEventVideos',
      'getEventQuestionMedia',
      'saveIncidentReportRenderOptions',
      'createIncidentReport',
      'checkIncidentReportProgress',
      'getIncidentReportHistory',
      'getIncidentReportPreview',
      'getEventDocuments',
      'getDamageRelatedExpenses',
      'loadClaims'
    ]),

    doLoadInitialData () {
      const opts = this.event.incidentReportRenderOptions || {}

      // Pre-select images immediately — synchronous from the event prop
      if (!opts.includedImageIds || opts.includedImageIds.length === 0) {
        this.renderOptions = { ...this.renderOptions, includedImageIds: this.allEventImages.map(i => i.id) }
      }

      // Load insurance claims in the background
      this.loadClaims(this.event.id)
        .then(() => {
          this.eventClaims = this.$store.state.events.claims || []
          if (!opts.includedInsuranceClaimIds || opts.includedInsuranceClaimIds.length === 0) {
            this.renderOptions = { ...this.renderOptions, includedInsuranceClaimIds: this.eventClaims.map(c => c.id) }
          }
        })
        .catch(() => {})

      // Load people in the background — update selection when ready
      this.getInvolvedPeople(this.event.id)
        .then(res => {
          if (res && res.data) {
            this.involvedPeople = res.data.involvedPeople || []
            if (!opts.includedPeopleIds || opts.includedPeopleIds.length === 0) {
              this.renderOptions = { ...this.renderOptions, includedPeopleIds: this.involvedPeople.map(p => p.id) }
            }
          }
        })
        .catch(() => {})

      // Load event-level videos + SOP question videos in parallel, then merge
      this.mediaLoading = true
      Promise.all([
        this.getEventVideos(this.event.id).catch(() => null),
        this.getEventQuestionMedia(this.event.id).catch(() => null)
      ]).then(([videosRes, questionMediaRes]) => {
        const eventVids = (videosRes && videosRes.data && videosRes.data.videos) || []
        const sopVids = (questionMediaRes && questionMediaRes.data && questionMediaRes.data.videos) || []
        this.eventVideos = [...eventVids, ...sopVids]
        if (!opts.includedVideoIds || opts.includedVideoIds.length === 0) {
          this.renderOptions = { ...this.renderOptions, includedVideoIds: this.eventVideos.map(v => v.id) }
        }
      }).catch(() => {}).finally(() => { this.mediaLoading = false })

      // Load event documents in the background — API returns ALL docs regardless of folder
      this.getEventDocuments(this.event.id)
        .then(res => {
          if (res && res.data) {
            this.eventDocuments = (res.data.documents || []).filter(d => d.typeName !== 'Incident Report')
            if (!opts.includedDocumentIds || opts.includedDocumentIds.length === 0) {
              this.renderOptions = { ...this.renderOptions, includedDocumentIds: this.eventDocuments.map(d => d.id) }
            }
          }
        })
        .catch(() => {})

      // Load expenses in the background
      this.getDamageRelatedExpenses(this.event.id)
        .then(res => {
          if (res && res.data) {
            this.eventExpenses = res.data.damageRelatedExpenses || []
            if (!opts.includedExpenseIds || opts.includedExpenseIds.length === 0) {
              this.renderOptions = { ...this.renderOptions, includedExpenseIds: this.eventExpenses.map(e => e.id) }
            }
          }
        })
        .catch(() => {})

      // Load report history in the background
      this.getIncidentReportHistory({ eventId: this.event.id })
        .then(historyRes => {
          if (historyRes && historyRes.data) {
            this.historyItems = historyRes.data.versions || []
          }
        })
        .catch(() => {})
    },

    async doPreviewReport () {
      this.isLoadingPreview = true
      this.previewHtml = null
      try {
        await this.saveIncidentReportRenderOptions({
          eventId: this.event.id,
          options: this.renderOptions
        })
        const response = await this.getIncidentReportPreview({
          eventId: this.event.id,
          renderOptions: this.renderOptions
        })
        if (response && response.data) {
          this.previewHtml = response.data
        }
        this.activeStep = 'preview'
      } catch (e) {
        this.$toasted && this.$toasted.error('Error generating preview. Please try again.', { duration: 4000 })
        console.error('Preview error:', e)
      } finally {
        this.isLoadingPreview = false
      }
    },

    async doGenerateReport () {
      this.isLoading = true
      try {
        // Save current render options first
        await this.saveIncidentReportRenderOptions({
          eventId: this.event.id,
          options: this.renderOptions
        })

        // Kick off PDF generation
        const result = await this.createIncidentReport({ eventId: this.event.id })
        const token = result && result.data && result.data.token
        if (!token) {
          throw new Error('No token received from server')
        }

        this.generatingToken = token
        this.pollAttempts = 0
        this.progressData = null
        this.activeStep = 'generating'
        this.startPolling()
      } catch (e) {
        this.$toasted && this.$toasted.error('Error starting report generation. Please try again.', { duration: 4000 })
        console.error('Generate report error:', e)
      } finally {
        this.isLoading = false
      }
    },

    startPolling () {
      this.stopPolling()
      this.pollInterval = setInterval(async () => {
        this.pollAttempts++
        if (this.pollAttempts > MAX_POLL_ATTEMPTS) {
          this.stopPolling()
          this.progressData = {
            ...(this.progressData || {}),
            isError: true,
            currentStepDescription: 'Report generation timed out. Please try again.'
          }
          this.activeStep = 'complete'
          return
        }

        try {
          const status = await this.checkIncidentReportProgress({
            eventId: this.event.id,
            token: this.generatingToken
          })
          if (status && status.data) {
            this.progressData = status.data
            if (status.data.isComplete || status.data.isError) {
              this.stopPolling()
              this.activeStep = 'complete'
              if (status.data.isComplete) {
                this.$emit('reportGenerated', status.data.generatedFileResourceId)
                // Refresh history
                const historyRes = await this.getIncidentReportHistory({ eventId: this.event.id }).catch(() => null)
                if (historyRes && historyRes.data) {
                  this.historyItems = historyRes.data.versions || []
                }
              }
            }
          }
        } catch (e) {
          console.error('Poll error:', e)
        }
      }, 3000)
    },

    stopPolling () {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },

    doViewReport () {
      if (this.progressData && this.progressData.generatedPublicShareUrl) {
        window.open(this.progressData.generatedPublicShareUrl, '_blank')
      } else if (this.progressData && this.progressData.generatedFileResourceId) {
        this.$emit('goToFiles')
      }
    },

    doGoToFiles () {
      this.$emit('goToFiles')
    },

    doGenerateAnother () {
      this.stopPolling()
      this.progressData = null
      this.generatingToken = null
      this.pollAttempts = 0
      this.previewHtml = null
      this.activeStep = 'selection'
    },

    doRegenerateFromHistory (historyItem) {
      if (historyItem.renderOptionsSnapshot) {
        this.renderOptions = {
          ...DEFAULT_RENDER_OPTIONS,
          ...historyItem.renderOptionsSnapshot
        }
      }
      this.activeStep = 'selection'
    }
  }
}
</script>

<style scoped>
.incident-report-wizard {
  background: #fff;
  min-height: 0;
}
.wizard-header {
  background-color: #343a40 !important;
}
.wizard-body {
  flex: 1;
  min-height: 0; /* required: overrides flex default min-height:auto so overflow-y actually scrolls */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>



// WEBPACK FOOTER //
// src/views/event/IncidentReportWizard.vue